import React from "react";
import axios from 'axios';
import ApiConfig from '../config/ApiConfig';
import reduxStore from "../redux/store/index";
import {logOutUser} from "../redux/actions/authActions";
import lscache from 'lscache';

const extranetApi = axios.create(ApiConfig);
const TTL_MINUTES = 15;

// Endpoints that must never trigger the global "401 => force logout + hard
// redirect" behaviour below.
//
// `mfa/*` legitimately returns 401/1000 while a pending MFA session is missing
// or expired (contract sections 4/5/7). Hard-redirecting there would reload the
// page and destroy the pending screen; each MFA screen handles its own 401 by
// clearing the pending marker and routing to sign-in in-app instead.
//
// `login` is here because a pending-session 401 during sign-in must not yank
// the user off the form they are filling in.
const NO_AUTH_REDIRECT_URLS = ['mfa/', 'login'];

const isAuthRedirectExempt = (url) => {
  if (!url) return false;
  return NO_AUTH_REDIRECT_URLS.some((needle) => url.indexOf(needle) !== -1);
};

// extranetApi's response interceptor (below) RETURNS http errors into the
// success chain instead of rethrowing them, so 4xx/5xx arrive at the caller's
// `.then` as resolved AxiosError objects - NOT as rejections. A resolved value
// from extranetApi is therefore one of:
//   - an axios response (`status`, `data`, ...) for 2xx, or
//   - an AxiosError (`isAxiosError`, envelope in `.response`) for 4xx/5xx and
//     network failures.
// Always check a resolved value with this before reading `status`/`data` off
// it. Returns the axios error when the value is one, null for a real response.
export const resolvedApiFailure = (response) => {
    if (response && response.isAxiosError) return response;
  return null;
};

// Error envelope is `{error: CODE, message: "..."}` (contract section 6). Some
// older endpoints instead answer 200 with `{success: false, data: {error}}`,
// so look in both places.
const errorBody = (error) => {
  const data = error && error.response && error.response.data;
  if (!data) return null;
  if (typeof data.error !== 'undefined' && data.error !== null) return data;
  if (data.data && typeof data.data.error !== 'undefined' && data.data.error !== null) return data.data;
  return null;
};

// Numeric contract error code, or null when the response carried none.
export const apiErrorCode = (error) => {
  const body = errorBody(error);
  if (!body) return null;
  const code = Number(body.error);
  return isNaN(code) ? null : code;
};

// Server-supplied message, falling back to caller-provided copy so every screen
// has something sensible to render.
export const apiErrorMessage = (error, fallback) => {
  const body = errorBody(error);
  if (body && body.message) return body.message;
  return fallback || 'Something went wrong. Please try again.';
};

// True when the server rejected us because the session (or pending MFA session)
// is gone. Callers treat this as "start over from the password prompt".
export const isUnauthorised = (error) => !!(
  error && error.response && error.response.status === 401
);

const {dispatch} = reduxStore; // direct access to redux store.
extranetApi.interceptors.response.use(response => {
  let user = reduxStore.getState().user;
  user.timeout = user.timeoutMinutes * 60;
  return response;
}, error => {
  // A call can opt out of the redirect (but not the recovery below) by
  // passing `skipAuthRedirect: true` in its request config - for screens that
  // own their 401 handling (e.g. probing a session that may legitimately be
  // pending). Anything else keeps the global behaviour.
  const skipRedirect = !!(error.config && error.config.skipAuthRedirect);
  if (
    error.response && error.response.status && error.response.status === 401 &&
    !skipRedirect &&
    !isAuthRedirectExempt(error.config && error.config.url)
  ) {
    // Unauthorised - Just get straight to the login page
    dispatch(logOutUser({}));
    window.location.href = '/extranet/auth/signin';
  }
  // Returning (not rethrowing) recovers the chain, so every HTTP error keeps
  // flowing to the caller's `.then` as an AxiosError - see resolvedApiFailure.
  // Do not change this to a throw without auditing every extranetApi caller.
  return error;
});

export {extranetApi, TTL_MINUTES};

// HOC for fetching data
export const withApi = (url, dispatchFn) => (Component) =>
  class WithApi extends React.Component {

    constructor(props) {
      super(props);
      this.state = {
        data: null,
        isLoading: false,
        error: null,
        url: url,
      };
      this.Api = extranetApi;
    }

    getData() {

      this.setState({
        ...this.state,
        isLoading: true
      }, function() {

        this.Api.get(this.state.url)
          .then(
            (response) => {
              // console.log('API :: ', response);
              if (response.status === 200) {
                if (response.data.success === true) {
                  this.setState({
                    ...this.state,
                    data: response.data.data,
                    isLoading: false,
                  }, function() {
                    // console.log('Loaded data = ', this.state.data);
                    lscache.set(this.state.url, this.state.data, TTL_MINUTES);
                  });
                  if (dispatchFn) {
                    this.props[dispatchFn](response.data.data);
                  }
                  // console.log(this.state);
                }
              }
            }
          )
          .catch((error) => {
            console.log(error);
            this.setState({
              ...this.state,
              error: error,
              isLoading: false,
            })
          });

      });

    }

    componentDidMount() {

      let localData = lscache.get(this.state.url);
      // console.log('localData', localData);
      if (typeof localData === 'string') {
        localData = JSON.parse(localData);
      }
      // console.log('localData', localData);
      // console.log('withAPI: localData', this.state.url, localData);
      if (localData) {
        this.setState({
          ...this.state,
          data: localData,
          isLoading: false,
        });
      } else {
        this.getData();
      }

    }

    componentDidUpdate(prevProps, prevState, snapshot) {
      // console.log('componentDidUpdate', prevProps, prevState, snapshot);
    }

    doRefresh() {
      // console.log('Refreshing that shit');
    }

    render() {
      return <Component { ...this.props } { ...this.state } doRefresh={this.doRefresh} />;
    }

  } // WithApi class
